import clsx from 'clsx';
import { useCallback, useMemo, useRef } from 'react';

import { SupportedMatchInfoHashCategory, WidgetContentLayout, WidgetItemAspectRatio } from '@vestico/model';

import { CustomCSSProperties, useElementSize } from '@vestico/shared';

import { LayoutLoadMoreHandler } from '../carousel/LayoutLoadMoreHandler';
import { FEED_LAYOUT_ITEM_BORDER_RADIUS_KEY, FeedItem } from '../common/FeedItem';
import { useCarouselStyles } from '../hooks/useCarouselStyles';
import { useInViewVideo } from '../hooks/useInViewVideo';
import { usePreviewTexts } from '../hooks/usePreviewTexts';
import { BaseLayoutProps } from '../model/baseLayoutProps';
import style from './FeedLayout.module.css';
import { InlineEnterMatchInfoCTA } from './InlineEnterMatchInfoCTA';

export interface FeedLayoutProps extends BaseLayoutProps {
  layout: WidgetContentLayout;
  category?: SupportedMatchInfoHashCategory;
}

export const FeedLayout = ({
  layout,
  responses,
  onResponseClick,
  showUserMatchInfoCTA,
  onBeginUserMatchInfo,
  loading,
  loadMore,
  category,
  clientID,
  hasMore,
}: FeedLayoutProps) => {
  const { showLikes, itemBorderRadius } = layout;

  const handleResponseClick = useCallback(
    (index: number) => {
      onResponseClick(index);
    },
    [onResponseClick]
  );

  const ref = useRef<HTMLDivElement>(null);

  const containerSize = useElementSize(ref);

  const previewTextsArray = usePreviewTexts(layout, responses);

  const aspectRatio: WidgetItemAspectRatio = useMemo(() => '3:4', []);

  const { firstInViewVideoResponse, updateInView } = useInViewVideo({ layout, responses });
  const onUpdateInView = useCallback((index: number) => (inView: boolean) => updateInView(index, inView), [updateInView]);

  const borderRadiusStyle: string = useMemo(
    () => `${itemBorderRadius !== undefined && !Number.isNaN(itemBorderRadius) ? itemBorderRadius : 12}px`,
    [itemBorderRadius]
  );
  const customCSS = useMemo(() => {
    const stylesOverrides: CustomCSSProperties = {};

    stylesOverrides[FEED_LAYOUT_ITEM_BORDER_RADIUS_KEY] = borderRadiusStyle;

    return stylesOverrides;
  }, [borderRadiusStyle]);

  const { styles } = useCarouselStyles({ aspectRatio, customCSS });

  const rows = useMemo(() => {
    if (containerSize.width < 0) {
      return null;
    }

    const feedItems = responses.map((response, index) => {
      return (
        <FeedItem
          key={response.postDocumentID}
          response={response}
          onClick={handleResponseClick}
          previewText={previewTextsArray[index]}
          index={index}
          showLikes={showLikes}
          aspectRatio={aspectRatio}
          updateInView={onUpdateInView(index)}
          playVideo={response === firstInViewVideoResponse}
          borderRadius={[borderRadiusStyle]}
        />
      );
    });

    if (showUserMatchInfoCTA && onBeginUserMatchInfo) {
      // Add CTA to enter body info every 6 posts
      for (let i = 1; i < feedItems.length; i += 6) {
        feedItems.splice(i, 0, <InlineEnterMatchInfoCTA onClick={onBeginUserMatchInfo} category={category} clientID={clientID} />);
      }
    }

    const loadMoreLayout = hasMore ? <LayoutLoadMoreHandler loading={loading} loadMore={loadMore} /> : null;
    return [...feedItems, loadMoreLayout];
  }, [
    containerSize.width,
    responses,
    showUserMatchInfoCTA,
    onBeginUserMatchInfo,
    hasMore,
    loading,
    loadMore,
    handleResponseClick,
    previewTextsArray,
    showLikes,
    aspectRatio,
    onUpdateInView,
    firstInViewVideoResponse,
    borderRadiusStyle,
    category,
    clientID,
  ]);

  return (
    <div className={clsx(style.root)} style={styles} ref={ref} role="list">
      {rows}
    </div>
  );
};
